import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  contactInputSchema,
  contactRoles,
  type ContactRole,
  type PropertyContact,
} from '@vuokramani/shared';
import { ContactValue, pageStyles as ps } from '../../components/page.tsx';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  SelectField,
  TextField,
} from '../../components/ui.tsx';
import { errorKey } from '../../lib/api.ts';
import { useContacts, useDeleteContact, useSaveContact } from '../../lib/tenancy.ts';
import s from './tenancy.module.css';

export function contactLabel(c: PropertyContact, t: (k: string) => string): string {
  return c.role === 'other' && c.roleLabel ? c.roleLabel : t(`contactRoles.${c.role}`);
}

/**
 * Taloyhtiön yhteystiedot listana. Vuokranantaja lisää, muokkaa ja valitsee näkyvyyden;
 * vuokralainen näkee vain hänelle näkyviksi merkityt (rajaus tehdään jo backendissä).
 */
export function ContactsList({ propertyId, editable }: { propertyId: string; editable: boolean }) {
  const { t } = useTranslation();
  const { data: contacts } = useContacts(propertyId);
  const save = useSaveContact(propertyId);
  const remove = useDeleteContact(propertyId);
  const [editing, setEditing] = useState<PropertyContact | 'new' | null>(null);
  const [deleting, setDeleting] = useState<PropertyContact | null>(null);

  const toggle = (c: PropertyContact) =>
    save.mutate({
      id: c.id,
      body: {
        role: c.role,
        roleLabel: c.roleLabel ?? '',
        name: c.name ?? '',
        phone: c.phone ?? '',
        email: c.email ?? '',
        visibleToTenant: !c.visibleToTenant,
      },
    });

  return (
    <>
      <div className={s.contactsHead}>
        <h3 className={s.contactsTitle}>{t('contacts.title')}</h3>
        {editable && (
          <Button variant="ghost" onClick={() => setEditing('new')}>
            <Plus size={16} aria-hidden="true" />
            {t('contacts.add')}
          </Button>
        )}
      </div>

      {contacts && contacts.length === 0 && <p className={s.empty}>{t('contacts.empty')}</p>}

      <ul className={s.list}>
        {contacts?.map((c) => (
          <li key={c.id} className={s.row}>
            <span className={s.rowLabel}>
              {contactLabel(c, t)}
              {editable && c.visibleToTenant && (
                <Eye
                  size={14}
                  className={ps.sharedIcon}
                  role="img"
                  aria-label={t('property.sharedWithTenant')}
                />
              )}
            </span>
            <span>
              <ContactValue name={c.name} phone={c.phone} email={c.email} />
              {editable && !c.visibleToTenant && (
                <span className={s.hiddenTag}>
                  <EyeOff size={13} aria-hidden="true" />
                  {t('contacts.hidden')}
                </span>
              )}
            </span>
            {editable ? (
              <span className={s.rowActions}>
                <Button
                  variant="ghost"
                  icon
                  onClick={() => toggle(c)}
                  aria-pressed={c.visibleToTenant}
                  aria-label={c.visibleToTenant ? t('contacts.hide') : t('contacts.show')}
                  title={c.visibleToTenant ? t('contacts.hide') : t('contacts.show')}
                >
                  {c.visibleToTenant ? (
                    <Eye size={16} className={s.shareOn} />
                  ) : (
                    <EyeOff size={16} />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  icon
                  onClick={() => setEditing(c)}
                  aria-label={t('edit.edit')}
                  title={t('edit.edit')}
                >
                  <Pencil size={16} />
                </Button>
                <Button
                  variant="ghost"
                  icon
                  onClick={() => setDeleting(c)}
                  aria-label={t('common.delete')}
                  title={t('common.delete')}
                >
                  <Trash2 size={16} />
                </Button>
              </span>
            ) : (
              <span />
            )}
          </li>
        ))}
      </ul>

      {editing && (
        <ContactDialog
          propertyId={propertyId}
          contact={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      <Dialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title={t('contacts.deleteTitle')}
      >
        <p>{deleting && t('contacts.deleteText', { label: contactLabel(deleting, t) })}</p>
        <DialogActions>
          <Button variant="ghost" onClick={() => setDeleting(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={remove.isPending}
            onClick={() =>
              deleting && remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
            }
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function ContactDialog({
  propertyId,
  contact,
  onClose,
}: {
  propertyId: string;
  contact: PropertyContact | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const save = useSaveContact(propertyId);
  const [role, setRole] = useState<ContactRole>(contact?.role ?? 'manager');
  const [roleLabel, setRoleLabel] = useState(contact?.roleLabel ?? '');
  const [name, setName] = useState(contact?.name ?? '');
  const [phone, setPhone] = useState(contact?.phone ?? '');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [visible, setVisible] = useState(contact?.visibleToTenant ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function submit(event: FormEvent) {
    event.preventDefault();
    const parsed = contactInputSchema.safeParse({
      role,
      roleLabel,
      name,
      phone,
      email,
      visibleToTenant: visible,
    });
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] ??= issue.message;
      setErrors(next);
      return;
    }
    save.mutate({ id: contact?.id, body: parsed.data }, { onSuccess: onClose });
  }
  const err = (k: string) => (errors[k] ? t(errors[k]) : undefined);

  return (
    <Dialog open onClose={onClose} title={contact ? t('contacts.editTitle') : t('contacts.add')}>
      <form onSubmit={submit} noValidate style={{ display: 'grid', gap: '1.1rem' }}>
        <SelectField
          label={t('contacts.role')}
          value={role}
          onChange={(e) => setRole(e.target.value as ContactRole)}
        >
          {contactRoles.map((r) => (
            <option key={r} value={r}>
              {t(`contactRoles.${r}`)}
            </option>
          ))}
        </SelectField>
        {role === 'other' && (
          <TextField
            label={t('contacts.roleLabel')}
            hint={t('contacts.roleLabelHint')}
            value={roleLabel}
            onChange={(e) => setRoleLabel(e.target.value)}
            error={err('roleLabel')}
          />
        )}
        <TextField
          label={t('edit.contactName')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={err('name')}
        />
        <TextField
          label={t('edit.phone')}
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={err('phone')}
        />
        <TextField
          label={t('edit.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={err('email')}
        />
        <label className={s.check}>
          <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} />
          <span>{t('contacts.visibleToTenant')}</span>
        </label>
        {save.isError && <Alert>{t(errorKey(save.error))}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={save.isPending}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
